/**
 * Shared display formatters.
 *
 * Home and Product Comparison render the same API fields (`current_distribution`,
 * `ncr_per_eu`, ...). They previously each carried a private copy of these helpers and
 * the copies disagreed: percentages came out as "12.3%" on one page and "12%" on the
 * other for the identical number. Anything formatting those fields imports from here.
 */

/** Currency with no cents, em-dash for absent values, U+2212 minus for negatives. */
export const money = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const amount = Number(value);
  return `${amount < 0 ? '−' : ''}$${Math.abs(amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

/**
 * Share as a percentage, to one decimal.
 *
 * One decimal rather than whole numbers: mix shares routinely differ by less than a
 * point, so rounding makes genuinely different products read identically and lets a
 * total row show "100%" when the shares sum to 99.6%.
 */
export const pct = (value: number | null | undefined): string =>
  `${(Number(value || 0) * 100).toFixed(1)}%`;

/** Change figure with an explicit sign ("+$1,200" / "−$86,619"), for "vs current" columns only. */
export const signedMoney = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const amount = Number(value);
  return `${amount < 0 ? '−' : '+'}$${Math.abs(amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

/** Whole-number count with thousands separators; em-dash for absent values. */
export const count = (value: number | null | undefined): string =>
  value === null || value === undefined || Number.isNaN(Number(value)) ? '—' : Math.round(Number(value)).toLocaleString('en-US');

/**
 * Invoice unit strength from ScenarioModelingCurrentDistribution.DisplayUnitEquivalency (the
 * default NDC's vial strength + UOM): "100.00 MG" -> "100 MG", "0.50 MG" -> "0.5 MG".
 * Unrecognised text is shown as stored; empty values give undefined.
 */
export const unitStrength = (raw: string | null | undefined): string | undefined => {
  const text = (raw ?? '').trim();
  if (!text || /^(none|null|undefined)$/i.test(text)) return undefined;
  const match = text.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
  if (!match) return text;
  const amount = Number(match[1]);
  return `${amount.toLocaleString('en-US', { maximumFractionDigits: 4 })}${match[2] ? ` ${match[2]}` : ''}`;
};

/**
 * Calendar date as "Sep 10, 2026". Date-only strings (YYYY-MM-DD) are read as local dates,
 * so they don't shift a day in time zones west of UTC.
 */
export const shortDate = (value: string | Date | null | undefined): string => {
  if (!value) return '—';
  const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00`) : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
